import React, { useState, useEffect } from "react"
import { Flex, Input, Stack, Text, Textarea } from "@chakra-ui/react"

type ProductGiftCardFormProps = {
  setRecipient: React.Dispatch<React.SetStateAction<string>>
  setName: React.Dispatch<React.SetStateAction<string>>
  setEmail: React.Dispatch<React.SetStateAction<string>>
  setMessage: React.Dispatch<React.SetStateAction<string>>
  setIsFormValid: React.Dispatch<React.SetStateAction<boolean>>
}

const ProductGiftCardForm: React.FC<ProductGiftCardFormProps> = ({ setRecipient, setName, setEmail, setMessage, setIsFormValid }) => {
  const [count, setCount] = useState(0)
  const [errors, setErrors] = useState({
    recipient: "",
    name: "",
    email: "",
    message: "",
  })
  const [touched, setTouched] = useState({
    recipient: false,
    name: false,
    email: false,
    message: false,
  })
  const [fields, setFields] = useState({
    recipient: "",
    name: "",
    email: "",
    message: "",
  })

  const validateInput = (value: string, field: string): boolean => {
    if (value.trim() === "") {
      setErrors(prev => ({ ...prev, [field]: "" }))
      return true
    }

    const englishOnlyRegex = /^[a-zA-Z0-9\s.,!?'-]*$/
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

    if (field === "email") {
      if (!emailRegex.test(value)) {
        setErrors(prev => ({ ...prev, [field]: "Invalid email format" }))
        return false
      }
    } else if (!englishOnlyRegex.test(value)) {
      setErrors(prev => ({
        ...prev,
        [field]: "Use letters A-Z and digits 0-9 only.",
      }))
      return false
    }

    setErrors(prev => ({ ...prev, [field]: "" }))
    return true
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: "recipient" | "name" | "email" | "message"
  ) => {
    const { value } = e.target
    setFields(prev => ({ ...prev, [field]: value }))
    switch (field) {
      case "recipient":
        setRecipient(value)
        break
      case "name":
        setName(value)
        break
      case "email":
        setEmail(value)
        break
      case "message":
        setMessage(value)
        setCount(value.length)
        break
    }
    if (touched[field]) {
      validateInput(value, field)
    }
  }

  const handleInputBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: "recipient" | "name" | "email" | "message"
  ) => {
    const { value } = e.target
    setTouched(prev => ({ ...prev, [field]: true }))
    validateInput(value, field)
  }

  useEffect(() => {
    const allFieldsTouched = Object.values(touched).every(value => value)
    const allFieldsValid = Object.values(errors).every(error => error === "")
    const allFieldsFilled = Object.values(fields).every(value => value.trim() !== "")
    setIsFormValid(allFieldsTouched && allFieldsValid && allFieldsFilled)
  }, [errors, touched, fields, setIsFormValid])

  return (
    <React.Fragment>
      <Stack gap={"24px"}>
        <Flex w="100%" gap={"16px"}>
          <Stack flexGrow={1}>
            <Text as="label" variant="label">
              To
            </Text>
            <Input
              px="15px"
              maxLength={30}
              type="text"
              placeholder="Recipient's name"
              value={fields.recipient}
              onChange={e => handleInputChange(e, "recipient")}
              onBlur={e => handleInputBlur(e, "recipient")}
            />
            {errors.recipient && touched.recipient && fields.recipient.trim() !== "" && (
              <Text color="red" fontSize="sm">
                {errors.recipient}
              </Text>
            )}
          </Stack>
          <Stack flexGrow={1}>
            <Text as="label" variant="label">
              From
            </Text>
            <Input
              px="15px"
              type="text"
              placeholder="Your name"
              value={fields.name}
              onChange={e => handleInputChange(e, "name")}
              onBlur={e => handleInputBlur(e, "name")}
              maxLength={30}
            />
            {errors.name && touched.name && fields.name.trim() !== "" && (
              <Text color="red" fontSize="sm">
                {errors.name}
              </Text>
            )}
          </Stack>
        </Flex>
        <Stack>
          <Text as="label" variant="label">
            Email
          </Text>
          <Input
            px="15px"
            type="email"
            placeholder="Recipient's Email"
            value={fields.email}
            onChange={e => handleInputChange(e, "email")}
            onBlur={e => handleInputBlur(e, "email")}
            maxLength={60}
          />
          {errors.email && touched.email && fields.email.trim() !== "" && (
            <Text color="red" fontSize="sm">
              {errors.email}
            </Text>
          )}
        </Stack>
        <Stack>
          <Text as="label" variant="label">
            Message
          </Text>
          <Textarea
            placeholder="Please enter your message"
            maxLength={201}
            value={fields.message}
            onChange={e => handleInputChange(e, "message")}
            onBlur={e => handleInputBlur(e, "message")}
          />
          {errors.message && touched.message && fields.message.trim() !== "" && (
            <Text color="red" fontSize="sm">
              {errors.message}
            </Text>
          )}
          <Text size="xs" color="#7B7670" mt={"0 !important"}>
            {`${count}/201 characters`}
          </Text>
        </Stack>
      </Stack>
    </React.Fragment>
  )
}

export default ProductGiftCardForm
